// Demonstration videos of « Séance ». DemoVideo dispatches a QUALIFIED source (video-fixtures.ts): the official YouTube iframe
// (YouTube's own controls) with a real fullscreen button on the wrapper, or the native player for a qualified file (HTML <video>,
// no dependency: play / pause, mute, seekable progress, time / duration from metadata, Fullscreen API with iOS fallback
// webkitEnterFullscreen, playsInline, no forced autoplay, no poster). Motion scoped to .cc-seance-player, reduced-motion in CSS.
import { useEffect, useRef, useState } from "react";
import { Maximize, Minimize, Pause, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import type { VideoSource } from "./video-fixtures";

type FsDoc = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => void };
type IosVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void; webkitDisplayingFullscreen?: boolean };

const fmt = (t: number) => { if (!Number.isFinite(t) || t < 0) return "0:00"; const s = Math.floor(t); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const fsElement = () => document.fullscreenElement ?? (document as FsDoc).webkitFullscreenElement ?? null;

type FileSource = Extract<VideoSource, { provider: "file" }>;
type YouTubeSource = Extract<VideoSource, { provider: "youtube" }>;

/** One entry point per qualified source: official YouTube iframe, or the native player for a file. */
export function DemoVideo({ video, label }: { video: VideoSource; label: string }) {
  return video.provider === "youtube" ? <YouTubeDemo video={video} label={label} /> : <VideoPlayer video={video} label={label} />;
}

/** Official YouTube embed. Play / pause, mute, progress, duration and YouTube's own fullscreen come from the YouTube controls
 *  (cross-origin: they cannot be restyled or read). Our fullscreen button puts the whole player in fullscreen with the
 *  Fullscreen API when the browser supports it on elements; otherwise (iOS Safari) it says to use the YouTube fullscreen
 *  button. Referrer policy strict-origin-when-cross-origin: YouTube refuses embeds sent without a referrer (error 153). */
function YouTubeDemo({ video, label }: { video: YouTubeSource; label: string }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [hint, setHint] = useState(false);
  useEffect(() => {
    const fs = () => setFullscreen(!!wrap.current && fsElement() === wrap.current);
    document.addEventListener("fullscreenchange", fs); document.addEventListener("webkitfullscreenchange", fs);
    return () => { document.removeEventListener("fullscreenchange", fs); document.removeEventListener("webkitfullscreenchange", fs); if (wrap.current && fsElement() === wrap.current) void (document.exitFullscreen?.() ?? (document as FsDoc).webkitExitFullscreen?.()); };
  }, []);
  const toggleFullscreen = () => {
    const w = wrap.current as FsEl | null; if (!w) return;
    if (fsElement()) { void (document.exitFullscreen?.() ?? (document as FsDoc).webkitExitFullscreen?.()); return; }
    if (w.requestFullscreen && document.fullscreenEnabled !== false) w.requestFullscreen().catch(() => setHint(true));
    else if (w.webkitRequestFullscreen) w.webkitRequestFullscreen();
    else setHint(true);
  };
  const src = `${video.embedUrl}${video.embedUrl.includes("?") ? "&" : "?"}playsinline=1&rel=0`;
  return <div className="cc-seance-player-yt-wrap">
    <div ref={wrap} className="cc-seance-player cc-seance-player-yt" data-fullscreen={fullscreen || undefined} role="group" aria-label={label}>
      <iframe className="cc-seance-player-video" src={src} title={`${label} (YouTube)`} loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
        allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
      <button type="button" className="cc-seance-player-fs" aria-label={fullscreen ? "Quitter le plein écran" : "Plein écran"} onClick={toggleFullscreen}>{fullscreen ? <Minimize size={16} strokeWidth={2.2} /> : <Maximize size={16} strokeWidth={2.2} />}</button>
    </div>
    <p className="cc-seance-meta"><a href={video.page} target="_blank" rel="noopener">{video.credit}</a></p>
    {hint && <p className="cc-seance-meta" role="status">Plein écran : utilise le bouton plein écran du lecteur YouTube.</p>}
  </div>;
}

function VideoPlayer({ video, label }: { video: FileSource; label: string }) {
  const wrap = useRef<HTMLDivElement>(null);
  const el = useRef<IosVideo>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "playing" | "paused" | "ended" | "error">("loading");
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const v = el.current; if (!v) return;
    const on: [string, () => void][] = [
      ["loadedmetadata", () => { setDuration(v.duration); setStatus(s => s === "loading" ? "ready" : s); }],
      ["durationchange", () => setDuration(v.duration)],
      ["timeupdate", () => setTime(v.currentTime)],
      ["play", () => setStatus("playing")],
      ["pause", () => setStatus(v.ended ? "ended" : "paused")],
      ["ended", () => setStatus("ended")],
      ["waiting", () => setWaiting(true)],
      ["playing", () => { setWaiting(false); setStatus("playing"); }],
      ["canplay", () => setWaiting(false)],
      ["volumechange", () => setMuted(v.muted)],
      ["error", () => { setWaiting(false); setStatus("error"); }],
      ["webkitbeginfullscreen", () => setFullscreen(true)],
      ["webkitendfullscreen", () => setFullscreen(false)],
    ];
    on.forEach(([n, f]) => v.addEventListener(n, f));
    if (v.readyState >= 1) { setDuration(v.duration); setStatus("ready"); } // metadata already there before listeners attached
    if (v.error) setStatus("error");
    const fs = () => setFullscreen(fsElement() === wrap.current);
    document.addEventListener("fullscreenchange", fs); document.addEventListener("webkitfullscreenchange", fs);
    return () => { on.forEach(([n, f]) => v.removeEventListener(n, f)); document.removeEventListener("fullscreenchange", fs); document.removeEventListener("webkitfullscreenchange", fs); };
  }, []);
  // Leaving the player (sheet closed, test stopped) exits our fullscreen.
  useEffect(() => () => { if (fsElement() && fsElement() === wrap.current) void (document.exitFullscreen?.() ?? (document as FsDoc).webkitExitFullscreen?.()); }, []);

  const toggle = () => {
    const v = el.current; if (!v || status === "error") return;
    if (v.paused || v.ended) { if (v.ended) v.currentTime = 0; v.play().catch(() => setStatus("error")); } else v.pause();
  };
  const seek = (t: number) => { const v = el.current; if (v && Number.isFinite(t)) { v.currentTime = t; setTime(t); } };
  const toggleMute = () => { const v = el.current; if (v) v.muted = !v.muted; };
  const toggleFullscreen = () => {
    const w = wrap.current as FsEl | null, v = el.current; if (!w || !v) return;
    if (fsElement()) { void (document.exitFullscreen?.() ?? (document as FsDoc).webkitExitFullscreen?.()); return; }
    if (w.requestFullscreen) w.requestFullscreen().catch(() => v.webkitEnterFullscreen?.());
    else if (w.webkitRequestFullscreen) w.webkitRequestFullscreen();
    else v.webkitEnterFullscreen?.(); // iOS Safari: fullscreen only exists on the <video> element itself.
  };
  const retry = () => { const v = el.current; if (!v) return; setStatus("loading"); v.load(); };

  const playing = status === "playing";
  const pct = duration > 0 ? Math.min(100, (time / duration) * 100) : 0;
  return <div ref={wrap} className="cc-seance-player" data-status={status} data-waiting={waiting || undefined} data-fullscreen={fullscreen || undefined} role="group" aria-label={label}>
    {/* src on the element itself (not <source>), so a failed load fires « error » on the video. */}
    <video ref={el} className="cc-seance-player-video" src={video.src} preload="metadata" playsInline onClick={toggle} />
    {status === "error"
      ? <div className="cc-seance-player-error" role="alert"><p>La vidéo n'a pas pu être chargée.</p><button type="button" onClick={retry}><RotateCcw size={14} strokeWidth={2.2} /> Réessayer</button></div>
      : <>
        {(status === "loading" || waiting) && <span className="cc-seance-player-spin" role="status" aria-label="Chargement de la vidéo" />}
        {!playing && status !== "loading" && <button type="button" className="cc-seance-player-big" aria-label={status === "ended" ? "Revoir la vidéo" : "Lire la vidéo"} onClick={toggle}>
          {status === "ended" ? <RotateCcw size={22} strokeWidth={2.2} /> : <Play size={22} strokeWidth={2.2} fill="currentColor" />}
        </button>}
        <div className="cc-seance-player-bar">
          <button type="button" aria-label={playing ? "Pause" : "Lire"} disabled={status === "loading"} onClick={toggle}>{playing ? <Pause size={16} strokeWidth={2.2} fill="currentColor" /> : <Play size={16} strokeWidth={2.2} fill="currentColor" />}</button>
          <span className="cc-seance-player-time">{fmt(time)}</span>
          <span className="cc-seance-player-track" style={{ ["--p" as string]: `${pct}%` }}>
            <input type="range" min={0} max={duration || 0} step={0.1} value={Math.min(time, duration || 0)} disabled={!duration} aria-label="Position dans la vidéo" aria-valuetext={`${fmt(time)} sur ${fmt(duration)}`} onChange={e => seek(Number(e.target.value))} />
          </span>
          <span className="cc-seance-player-time">{duration ? fmt(duration) : "–:––"}</span>
          <button type="button" aria-label={muted ? "Activer le son" : "Couper le son"} aria-pressed={muted} onClick={toggleMute}>{muted ? <VolumeX size={16} strokeWidth={2.2} /> : <Volume2 size={16} strokeWidth={2.2} />}</button>
          <button type="button" aria-label={fullscreen ? "Quitter le plein écran" : "Plein écran"} onClick={toggleFullscreen}>{fullscreen ? <Minimize size={16} strokeWidth={2.2} /> : <Maximize size={16} strokeWidth={2.2} />}</button>
        </div>
      </>}
  </div>;
}
